import { nanoid as generate } from 'nanoid';

import { Thunk, ActionPayload } from 'store/types';
import { setError } from 'store/ui/activities';
import { LanguageType, StorageKeys } from 'types/index';

import { ActionTypes, GetLanguageAction } from './types';
import { getMessageFromError, unknownErrorMessage } from 'store/utils';

const getLanguageAction = (payload: ActionPayload<GetLanguageAction>): GetLanguageAction => ({
	type: ActionTypes.GET_LANGUAGE,
	payload
});

/**
 * Fetches and stores the translation dictionary for the given language.
 *
 * The language itself is owned by the shell app and exposed through
 * `CommunicationBridgeProvider` / `useLanguage`. This thunk only cares
 * about loading the matching translations into Redux.
 */
export const getLanguage =
	(language: LanguageType): Thunk =>
	async dispatch => {
		try {
			const code = language.toLowerCase();
			const cacheKey = `${StorageKeys.Translations}:${language}`;

			const cached = localStorage.getItem(cacheKey);
			if (cached) {
				dispatch(getLanguageAction({ translations: JSON.parse(cached) }));
			}

			// No-cache ensures that the browser will send a conditional request with If-None-Match
			const response = await fetch(`/locales/${code}/translation.json`, {
				cache: 'no-cache'
			});
			const translations = await response.json();

			dispatch(getLanguageAction({ translations }));
			localStorage.setItem(cacheKey, JSON.stringify(translations));
		} catch (e: any) {
			const errorMessage = getMessageFromError(e);
			await dispatch(
				setError({
					type: ActionTypes.GET_LANGUAGE,
					error: errorMessage,
					toast: {
						display: errorMessage !== unknownErrorMessage,
						message: errorMessage
					},
					uuid: generate()
				})
			);
		}
	};
